import { ApiError, getSelectedContext, getStaffSession } from "@/lib/api";
import type {
  QRAlreadyResolvedDetails,
  QRItemSummaryLine,
  QRPendingOrder,
  QRResolvedVia,
} from "@/types/qr-orders";

/** This person's staff profile id (`profile.id` from the PIN verify; `profile_id` from older builds). */
export function myProfileId(): string | null {
  const profile = getStaffSession()?.profile;
  return profile?.id ?? profile?.profile_id ?? getSelectedContext()?.staffProfile?.id ?? null;
}

/** "Table 7", or "QR order" when the server left the table out. */
export const qrTableLabel = (tableNumber: string | null | undefined): string =>
  tableNumber ? `Table ${tableNumber}` : "QR order";

/** The accept window the server gives every QR order (accept_deadline - submitted_at). */
export const QR_ACCEPT_WINDOW_MS = 20_000;

/** Lines the Accept prompt lists before "+N more" (the server's item_summary size). */
export const QR_SUMMARY_LINES = 6;

/**
 * Server clock minus device clock (ms) from a server timestamp. `deviceAt` is
 * the device time the server stamp stands for: for a request, the midpoint of
 * asking and answering (the server stamps it somewhere in between).
 */
export function skewFrom(serverTime: string | null | undefined, deviceAt: number): number | null {
  if (!serverTime) return null;
  const server = Date.parse(serverTime);
  return Number.isFinite(server) ? server - deviceAt : null;
}

/** The device time a response's server stamp stands for: halfway between asking and answering. */
export const midpoint = (requestedAt: number, receivedAt: number): number => (requestedAt + receivedAt) / 2;

/** Milliseconds left until `deadlineAt` (server clock), never below 0. */
export function remainingMs(deadlineAt: number, deviceNow: number, skewMs: number): number {
  return Math.max(0, deadlineAt - (deviceNow + skewMs));
}

/** Whole seconds shown on the countdown (14.2 s left reads "15s", so 0 means it is up). */
export const secondsLeft = (ms: number): number => Math.ceil(ms / 1000);

/** The summary lines of a pending order, from item_summary or (older servers) its items. */
export function summaryOf(order: Pick<QRPendingOrder, "item_summary" | "more_count" | "items">): {
  lines: QRItemSummaryLine[];
  more: number;
} {
  if (order.item_summary && order.item_summary.length > 0) {
    return { lines: order.item_summary, more: order.more_count ?? 0 };
  }
  const all = (order.items ?? []).map((i) => ({
    name: i.item_name ?? "Item",
    quantity: i.quantity,
    variant: i.variant_name ?? null,
  }));
  return { lines: all.slice(0, QR_SUMMARY_LINES), more: Math.max(0, all.length - QR_SUMMARY_LINES) };
}

/**
 * Who resolved an order, in a few words: "Accepted by Ravi", "Auto-accepted",
 * "Rejected by Ravi", "Table closed" (the auto-reject when the table ended).
 * "by you" when `resolved_by_id` is `meId`.
 */
export function resolvedLabel(
  r: {
    status: "approved" | "rejected" | string;
    resolved_via?: QRResolvedVia | null;
    resolved_by_id?: string | null;
    resolved_by_name?: string | null;
  },
  meId?: string | null,
): string {
  const mine = !!meId && !!r.resolved_by_id && r.resolved_by_id === meId;
  const by = mine ? " by you" : r.resolved_by_name ? ` by ${r.resolved_by_name}` : "";
  if (r.status === "approved") return r.resolved_via === "auto" ? "Auto-accepted" : `Accepted${by}`;
  if (r.status === "rejected") return r.resolved_via === "auto" ? "Table closed" : `Rejected${by}`;
  return "Already handled";
}

/** The `details` of a 409 QR_ORDER_ALREADY_RESOLVED, or null for any other error. */
export function alreadyResolved(error: unknown): QRAlreadyResolvedDetails | null {
  if (!(error instanceof ApiError) || error.code !== "QR_ORDER_ALREADY_RESOLVED") return null;
  const details = (error.data as { details?: Partial<QRAlreadyResolvedDetails> } | undefined)?.details;
  return {
    status: details?.status === "rejected" ? "rejected" : "approved",
    resolved_via: details?.resolved_via ?? null,
    resolved_by_id: details?.resolved_by_id ?? null,
    resolved_by_name: details?.resolved_by_name ?? null,
  };
}

/** No answer from the server (offline, timed out): the accept may or may not have happened. */
export function isNetworkError(error: unknown): boolean {
  return !(error instanceof ApiError) || error.status === 0;
}

/** Said when an accept got no answer: it may have gone through without this device printing. */
export const ACCEPT_UNKNOWN_MESSAGE = "Check the table's order: the KOT may not have printed.";

/** The order is gone (404 on accept). */
export const ORDER_REMOVED_LABEL = "This order was removed";

/** A short line for an accept that failed for another reason. */
export function acceptErrorMessage(error: unknown): string {
  if (isNetworkError(error)) return ACCEPT_UNKNOWN_MESSAGE;
  const code = error instanceof ApiError ? error.code : undefined;
  if (code === "SESSION_ENDED") return "The table was closed.";
  if (code === "ORDER_CLOSED") return "The table's order is closed.";
  if (code === "FORBIDDEN" || (error instanceof ApiError && error.status === 403)) {
    return "You can't accept QR orders.";
  }
  if (code === "NOT_FOUND" || (error instanceof ApiError && error.status === 404)) return `${ORDER_REMOVED_LABEL}.`;
  return "Couldn't accept. Try again.";
}
